import type { AvailabilityDay, Period, PortalRequestBody } from '@bbbk/schema';
import { ApiError } from '../lib/api';

// request-a-stay — the wizard's own helpers. LAYOUT AND WORDS ONLY.
//
// Every judgement about a date — today, a closed day, a Sunday, Christmas, a
// full night — is the server's, and arrives in the availability answer
// (AC24). These lay a month out, name a day, join dog names and remember a
// send; none of them decides whether a date can be booked. Calendar
// arithmetic here is done at noon UTC on an ISO date, so no device clock and
// no timezone ever moves a day.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const noon = (iso: string) => new Date(`${iso}T12:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);

export function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

export function monthEnd(start: string): string {
  const d = noon(start);
  d.setUTCMonth(d.getUTCMonth() + 1, 0);
  return iso(d);
}

export function addMonths(start: string, n: number): string {
  const d = noon(start);
  d.setUTCMonth(d.getUTCMonth() + n, 1);
  return iso(d);
}

export function monthTitle(start: string): string {
  const d = noon(start);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** The month's days, Monday first, with nulls before the 1st. */
export function monthCells(start: string): (string | null)[] {
  const first = noon(start);
  const lead = (first.getUTCDay() + 6) % 7;
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  const end = monthEnd(start);
  for (let d = start; d <= end; ) {
    cells.push(d);
    const n = noon(d);
    n.setUTCDate(n.getUTCDate() + 1);
    d = iso(n);
  }
  return cells;
}

/**
 * The prototype's classes for one calendar cell, from the state the server
 * sent (`c6`). A closed day is struck through whatever its level — it is not
 * a capacity state. The two ends are `sel`, the nights between `in`; a limited
 * end keeps its amber dot, a Sunday keeps its PM.
 */
/** Which end of the stay the next calendar tap sets. */
export type StayEnd = 'dropOff' | 'collection';

/**
 * THE END IN FOCUS — the one the customer chose by tapping Drop off or Collect,
 * or, with only an arrival picked, the collection the next tap will set.
 * Nothing picked, or both picked and neither chosen: none.
 */
export function endInFocus(range: PickedRange, chosen: StayEnd | null): StayEnd | null {
  if (chosen) return chosen;
  return range.checkIn && !range.checkOut ? 'collection' : null;
}

export function cellClass(day: string, a: AvailabilityDay | undefined, checkIn: string | null, checkOut: string | null, focus: StayEnd | null = null): string {
  if (!a) return '';
  if (a.level === 'past') return 'past';
  const end = day === checkIn || day === checkOut;
  const between = !!checkIn && !!checkOut && day > checkIn && day < checkOut;
  const out: string[] = [];
  if (end) out.push('sel');
  else if (between) out.push('in');
  if (a.handover === 'closed') {
    out.push('x');
  } else if (!end && !between) {
    out.push(a.level === 'full' ? 'r' : a.level === 'limited' ? 'a' : 'g');
  } else if (a.level === 'limited' || a.level === 'full') {
    // INSIDE THE STAY A CONDITION STILL SHOWS (Freddi, 2026-10-08) — quieter
    // than outside it, but never dropped: a full night in the band is the
    // reason the stay cannot send.
    out.push(a.level === 'full' ? 'r' : 'a');
  }
  if (a.handover === 'pm_only') out.push('pm');
  // A NIGHT OF A STAY THEY ALREADY HAVE (Freddi, 2026-10-08) — marked quietly,
  // over whatever else the day is.
  if (a.mine) out.push('mine');
  // NOTHING OUTSIDE THE STAY RECEDES (Freddi, 2026-10-08): every future day
  // keeps its own look; the stay stands out by its fill and its weight. With
  // an end in focus, the end being changed is ringed.
  if ((focus === 'dropOff' && day === checkIn) || (focus === 'collection' && day === checkOut && !!checkOut)) out.push('edit');
  return out.join(' ');
}

export interface PickedRange {
  checkIn: string | null;
  checkOut: string | null;
}

/**
 * The first tap is the arrival; the next one after it, the collection; anything
 * else starts again. With an end in focus the tap moves THAT end and keeps the
 * other — unless it would cross it, when the stay starts again from that day.
 */
export function pickDay(cur: PickedRange, day: string, focus: StayEnd | null = null): PickedRange {
  if (focus === 'dropOff' && cur.checkOut && day < cur.checkOut) return { checkIn: day, checkOut: cur.checkOut };
  if (focus === 'collection' && cur.checkIn && day > cur.checkIn) return { checkIn: cur.checkIn, checkOut: day };
  if (!focus && cur.checkIn && !cur.checkOut && day > cur.checkIn) return { checkIn: cur.checkIn, checkOut: day };
  return { checkIn: day, checkOut: null };
}

/** The line under *When?* — what the next tap does, said before it happens. */
export function stepLead(range: PickedRange, chosen: StayEnd | null): string {
  if (chosen === 'dropOff') return 'Tap the new drop-off day.';
  if (chosen === 'collection') return 'Tap the new collection day.';
  if (!range.checkIn) return 'Tap the day they arrive, then the day you collect.';
  if (!range.checkOut) return 'Now tap the day you collect.';
  return 'To change a day, tap Drop off or Collect below.';
}

/**
 * A SEND THAT MAY HAVE LANDED IS KEPT (PR #242 review). After an unreachable
 * send the request may already be with the kennel; leaving must not forget it,
 * or a fresh request books the stay twice. Home picks the kept send up on its
 * next load and asks again with the same id, which the server answers with the
 * booking if it landed.
 */
export function keepsSendOnLeave(failure: RequestFailure | null): boolean {
  return failure === 'unreachable';
}

/** Nothing entered yet — leaving loses nothing, so it asks nothing. */
export function isUntouched(step: number, answers: Record<string, DogStayAnswer>, note: string): boolean {
  if (step !== 1 || note.trim()) return false;
  return Object.values(answers).every((a) => a.meds === 'none' && !a.instructions.trim() && !a.keepApart);
}

/** `2026-10-17` → `Sat 17 Oct`. */
export function longDay(day: string): string {
  const d = noon(day);
  return `${DAY[d.getUTCDay()]} ${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
}

/** `2026-10-17` → `Sat 17`. */
export function shortDay(day: string): string {
  const d = noon(day);
  return `${DAY[d.getUTCDay()]} ${d.getUTCDate()}`;
}

export function dogNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

export function money(n: number): string {
  return Number.isInteger(n) ? `€${n}` : `€${n.toFixed(2)}`;
}

/** The deposit's share of the bar, one decimal, truncated as the prototype writes it (55.5%). */
export function splitWidth(deposit: number, total: number): string {
  if (total <= 0 || deposit >= total) return '100%';
  return `${Math.floor((deposit / total) * 1000) / 10}%`;
}

const PERIOD_WORD: Record<Period, string> = { am: 'morning', pm: 'afternoon' };
export const periodWord = (p: Period) => PERIOD_WORD[p];

function dayMonth(day: string): { weekday: string; date: number; month: string } {
  const d = noon(day);
  return { weekday: DAY[d.getUTCDay()]!, date: d.getUTCDate(), month: MONTHS[d.getUTCMonth()]! };
}

/** *Booking requested*'s three lines — when, the handovers, the money (`c28`). */
export function sentLines(r: {
  checkIn: string; checkOut: string; dropOff: Period; collection: Period; nights: number; total: number; deposit: number; inFull: boolean;
}): string[] {
  const a = dayMonth(r.checkIn);
  const b = dayMonth(r.checkOut);
  const when =
    a.month === b.month
      ? `${a.weekday} ${a.date} – ${b.weekday} ${b.date} ${b.month}`
      : `${a.weekday} ${a.date} ${a.month} – ${b.weekday} ${b.date} ${b.month}`;
  return [
    `${when} · ${r.nights} ${r.nights === 1 ? 'night' : 'nights'}`,
    `Drop off in the ${PERIOD_WORD[r.dropOff]}, collect in the ${PERIOD_WORD[r.collection]}`,
    r.inFull ? `${money(r.total)} total · due in full once accepted` : `${money(r.total)} total · ${money(r.deposit)} deposit due once accepted`,
  ];
}

/**
 * THE DOGS ARE PATRICIA'S RECORDS, SAID AS AN INVITATION (Freddi, 2026-10-13: "if you would like to
 * change any details about your dogs, send patricia a message"). One note, pinned where the dogs are
 * shown — the booking's dogs step and the dogs drawer — in the notes' own one-row form.
 */
// One row, whole, at 360px — the notes' own rule — so Freddi's sentence in its shortest true form.
export const DOGS_DETAILS_NOTE = { tone: 'mute', headline: 'Changing your dogs’ details?', body: 'message Patricia' } as const;

export interface DogStayAnswer {
  meds: 'none' | 'yes';
  instructions: string;
  keepApart: boolean;
}

export function buildRequestBody(args: {
  requestId: string;
  checkIn: string;
  checkOut: string;
  dropOff: Period;
  collection: Period;
  answers: Record<string, DogStayAnswer>;
  dogIds: readonly string[];
  note: string;
}): PortalRequestBody {
  return {
    requestId: args.requestId,
    checkIn: args.checkIn,
    checkOut: args.checkOut,
    dropOff: args.dropOff,
    collection: args.collection,
    dogs: args.dogIds.map((id) => {
      const a = args.answers[id] ?? { meds: 'none', instructions: '', keepApart: false };
      return { id, meds: a.meds, instructions: a.meds === 'yes' ? a.instructions.trim() : null, keepApart: a.keepApart };
    }),
    note: args.note.trim() || null,
  };
}

// ----- the send whose reply was lost (AC22) ---------------------------------
//
// The body goes into sessionStorage BEFORE it is sent and comes out when the
// reply lands. A reload in between finds it and sends the same id again; the
// server answers with the booking that exists. A store that throws — a private
// window, storage refused — loses only that safety net, never the send.

const PENDING = 'bbbk:pending-request';

export function savePending(body: PortalRequestBody): void {
  try {
    sessionStorage.setItem(PENDING, JSON.stringify(body));
  } catch {
    /* the net is gone; the send is not */
  }
}

export function readPending(): PortalRequestBody | null {
  try {
    const raw = sessionStorage.getItem(PENDING);
    if (!raw) return null;
    const v = JSON.parse(raw) as PortalRequestBody;
    return typeof v?.requestId === 'string' ? v : null;
  } catch {
    return null;
  }
}

export function clearPending(): void {
  try {
    sessionStorage.removeItem(PENDING);
  } catch {
    /* nothing to clear */
  }
}

// ----- what a failure says (AC23) -------------------------------------------

export type RequestFailure = 'signed-out' | 'no-household' | 'refused' | 'busy' | 'unreachable';

/**
 * ONLY A 401 IS "SIGNED OUT" (row 3's lesson: a read failure that was not a
 * 401 told the customer they were signed out). A 403 is the household being
 * gone; a 409 `dates_refused` is the server judging the dates again (AC11); a
 * 429 is the limiter. Everything else — a 500, a gateway, a dropped
 * connection — is *we can't reach the kennel*, with a retry.
 */
export function failureOf(e: unknown): RequestFailure {
  if (e instanceof ApiError) {
    if (e.status === 401) return 'signed-out';
    if (e.status === 403 && e.code === 'no_household') return 'no-household';
    if (e.status === 409 && e.code === 'dates_refused') return 'refused';
    if (e.status === 429) return 'busy';
  }
  return 'unreachable';
}
